import React, { useState, useEffect } from 'react'
import Layout from '@/components/Layout'
import Heading from '@/components/ui/Heading'
import SlideIn from '@/components/animations/SlideIn'
import { getAllProjects, getAllTechnologies, getSiteSettings } from '@/lib/api'
import { Project, Technology, SiteSettings } from '@/types'
import Link from 'next/link'
import Image from 'next/image'
import { urlFor } from '@/lib/sanity'
import { motion } from 'framer-motion'

const HomePage = () => {
  const [projects, setProjects] = useState<Project[]>([])
  const [technologies, setTechnologies] = useState<Technology[]>([])
  const [siteSettings, setSiteSettings] = useState<Partial<SiteSettings> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, technologiesData, settings] = await Promise.all([
          getAllProjects(),
          getAllTechnologies(),
          getSiteSettings(),
        ])

        // Get only featured projects (first 3)
        setProjects(projectsData.slice(0, 3))

        // Group technologies by category
        setTechnologies(technologiesData)

        setSiteSettings(settings)
        setIsLoading(false)
      } catch (error) {
        console.error('Error fetching data:', error)
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  // Group technologies by category
  const groupedTechnologies = technologies.reduce(
    (acc, tech) => {
      if (!acc[tech.category]) {
        acc[tech.category] = []
      }
      acc[tech.category].push(tech)
      return acc
    },
    {} as Record<string, Technology[]>
  )

  // Get blockchain-specific skills
  const blockchainSkills = technologies.filter(
    tech =>
      tech.category === 'Blockchain' ||
      tech.category === 'Smart Contracts' ||
      tech.category === 'Web3'
  )

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <SlideIn direction="left" duration={0.7}>
                <Heading level={1} gradient={true} className="mb-6">
                  {siteSettings?.author || 'Blockchain Developer'}
                </Heading>

                <p className="text-xl mb-8 max-w-2xl">
                  {siteSettings?.description ||
                    'Showcasing innovative blockchain projects, smart contract development, and web3 expertise.'}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link href="/projects" className="btn btn-primary">
                    View Projects
                  </Link>
                  <Link href="/contact" className="btn btn-outline">
                    Contact Me
                  </Link>
                </div>
              </SlideIn>
            </div>

            <div className="md:w-1/2 flex justify-center">
              <SlideIn direction="right" duration={0.7}>
                <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-primary shadow-lg">
                  {siteSettings?.profileImage ? (
                    <Image
                      src={urlFor(siteSettings.profileImage).width(400).height(400).url()}
                      alt={siteSettings.profileImage.alt || 'Profile Image'}
                      fill
                      className="object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-4xl font-bold">
                      {siteSettings?.author?.charAt(0) || 'B'}
                    </div>
                  )}
                </div>
              </SlideIn>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <SlideIn direction="up" delay={0.2}>
            <Heading level={2} className="text-center mb-12">
              Featured Projects
            </Heading>
          </SlideIn>

          {isLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <SlideIn
                  key={project._id}
                  direction="up"
                  delay={0.2 + index * 0.1}
                  className="h-full"
                >
                  <Link href={`/projects/${project.slug.current}`} className="block h-full">
                    <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
                      <div className="relative h-48">
                        {project.featuredImage ? (
                          <Image
                            src={urlFor(project.featuredImage).width(600).height(400).url()}
                            alt={project.featuredImage.alt || project.title}
                            fill
                            className="object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                            <span className="text-gray-500 dark:text-gray-400">No image</span>
                          </div>
                        )}
                      </div>

                      <div className="p-6 flex-grow">
                        <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                          {project.description?.[0]?.children?.[0]?.text ||
                            'No description available'}
                        </p>

                        <div className="flex flex-wrap gap-2 mt-auto">
                          {project.technologies?.slice(0, 3).map(tech => (
                            <span
                              key={tech._id}
                              className="inline-block bg-gray-100 dark:bg-gray-600 rounded-full px-3 py-1 text-sm"
                            >
                              {tech.name}
                            </span>
                          ))}
                          {project.technologies?.length > 3 && (
                            <span className="inline-block bg-gray-100 dark:bg-gray-600 rounded-full px-3 py-1 text-sm">
                              +{project.technologies.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </SlideIn>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600 dark:text-gray-300">No projects available yet.</p>
            </div>
          )}

          <div className="mt-12 text-center">
            <Link href="/projects" className="btn btn-outline">
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      {/* Skills Highlight Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <SlideIn direction="up" delay={0.3}>
            <Heading level={2} className="text-center mb-12">
              Skills & Expertise
            </Heading>
          </SlideIn>

          {isLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Blockchain Skills Highlight */}
              <div className="mb-16">
                <Heading level={3} className="mb-8 text-center">
                  Blockchain Expertise
                </Heading>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {blockchainSkills.map((skill, index) => (
                    <motion.div
                      key={skill._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                      className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md text-center"
                    >
                      <div className="w-16 h-16 mx-auto mb-4 relative">
                        {skill.icon ? (
                          <Image
                            src={urlFor(skill.icon).width(100).height(100).url()}
                            alt={skill.name}
                            fill
                            className="object-contain"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <span className="text-xl font-bold">{skill.name.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <h4 className="font-bold text-lg mb-2">{skill.name}</h4>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mb-1">
                        <div
                          className="bg-primary h-2.5 rounded-full"
                          style={{ width: `${(skill.proficiencyLevel / 5) * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {skill.yearsOfExperience} {skill.yearsOfExperience === 1 ? 'year' : 'years'}{' '}
                        experience
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Other Skills Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {Object.entries(groupedTechnologies)
                  .filter(
                    ([category]) =>
                      category !== 'Blockchain' &&
                      category !== 'Smart Contracts' &&
                      category !== 'Web3'
                  )
                  .slice(0, 2) // Show only 2 categories
                  .map(([category, skills]) => (
                    <SlideIn key={category} direction="up" delay={0.4} className="h-full">
                      <div className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-md h-full">
                        <h3 className="text-xl font-bold mb-6">{category}</h3>
                        <div className="space-y-4">
                          {skills.slice(0, 5).map(skill => (
                            <div key={skill._id}>
                              <div className="flex justify-between mb-1">
                                <span className="font-medium">{skill.name}</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {skill.proficiencyLevel}/5
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                                <div
                                  className="bg-primary h-2.5 rounded-full"
                                  style={{ width: `${(skill.proficiencyLevel / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </SlideIn>
                  ))}
              </div>

              <div className="mt-12 text-center">
                <Link href="/about" className="btn btn-outline">
                  View All Skills & Experience
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  )
}

export default HomePage
